import { useEffect, useRef, useState } from 'react';
import { Keyboard, Zoom } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import {
    IProductItemViewModel,
    IProductPageViewModel,
    IVariantImageViewModel,
} from 'Types/Litium';
import ProductImageSlide from 'Pages/ProductDetailPage/ProductImageSlide';
import Thumbnails from 'Pages/ProductDetailPage/ProductImageThumbnails';
import ProductSliderNavigation from 'Pages/ProductDetailPage/ProductSliderNavigation';
import ProductSliderPagination from 'Pages/ProductDetailPage/ProductSliderPagination';
import ProductVideoSlide from 'Pages/ProductDetailPage/ProductVideoSlide';
import { ISlideModel } from './ProductGalleryContainer';

const ProductImageContainer = ({
    slides,
    thumbnails,
    translations,
    videoUrl,
}: {
    videoUrl: IProductItemViewModel['videoUrl'];
    slides: ISlideModel[];
    thumbnails: IVariantImageViewModel[];
    translations: IProductPageViewModel['translations'];
}) => {
    const swiperRef = useRef<SwiperClass | null>(null);
    const [, setSignal] = useState({});
    const forceRerender = () => setSignal({});
    const [zoomID, setZoomID] = useState<string[]>([]);

    useEffect(() => {
        swiperRef.current?.slideToLoop(0);
    }, [thumbnails]);

    useEffect(() => {
        if (swiperRef.current) {
            swiperRef.current.on('slideChange', forceRerender);
            return () => {
                swiperRef.current.off('slideChange', forceRerender);
            };
        }
    }, [swiperRef]);

    const srcSetSizes = {
        default: { height: 522, width: 368 },
        md: { height: 1050, width: 740 },
        lg: { height: 624, width: 440 },
        xl: {
            height: 919,
            width: 648,
        },
        xxl: {
            height: 1339,
            width: 994,
        },
    };

    return (
        <>
            <div>
                <Swiper
                    onSwiper={swiper => {
                        swiperRef.current = swiper;
                        forceRerender();
                    }}
                    spaceBetween={15}
                    className="relative flex w-full [&.swiper-initialized_.swiper-slide:not(.swiper-slide-visible)]:invisible"
                    modules={[Keyboard, Zoom]}
                    initialSlide={0}
                    userAgent="Server"
                    slidesPerView="auto"
                    keyboard={{
                        enabled: true,
                    }}
                    loop
                    autoHeight={false}
                    wrapperClass="flex"
                    speed={600}
                    watchSlidesProgress
                    wrapperTag="ul"
                    lazyPreloaderClass="custom-preloader"
                    zoom
                    onZoomChange={(swiper, scale) => {
                        if (
                            scale > 1 &&
                            !zoomID.includes(slides[swiper.activeIndex].src)
                        ) {
                            setZoomID([
                                ...zoomID,
                                slides[swiper.activeIndex].src,
                            ]);
                        }
                    }}
                >
                    {slides.map((obj, index) => (
                        <SwiperSlide
                            tag="li"
                            key={`${obj.src}-${index}`}
                            className="transition-[visibility] duration-[600ms]"
                        >
                            {obj.videoUrl ? (
                                <ProductVideoSlide
                                    url={videoUrl}
                                    image={obj}
                                    srcSetSizes={srcSetSizes}
                                />
                            ) : (
                                <ProductImageSlide
                                    hasZoomedIn={zoomID.includes(obj.src)}
                                    index={index}
                                    data={obj}
                                    srcSetSizes={srcSetSizes}
                                />
                            )}
                        </SwiperSlide>
                    ))}
                    <ProductSliderNavigation
                        translations={translations}
                        swiper={swiperRef.current}
                    />
                </Swiper>
            </div>
            <ProductSliderPagination
                translations={translations}
                swiper={swiperRef.current}
                disabled={false}
                slides={slides}
                hasVideo={!!videoUrl}
            />
            {thumbnails.length > 0 && (
                <div className="hidden lg:grid">
                    <h3 className="my-5 text-center font-montserrat text-xl text-black">
                        {translations.moreColors}
                    </h3>
                    <Thumbnails swiper={swiperRef.current} />
                </div>
            )}
        </>
    );
};

export default ProductImageContainer;
